import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePen } from 'lucide-react';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, FormSkeleton, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';

interface Draft {
  id: string;
  diagnosis: string | null;
  procedure: string | null;
  notes: string | null;
}

export function PreAuthEditPage() {
  const { preAuthId = '' } = useParams<{ preAuthId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('preauth'));
  const detail = useQuery({ queryKey: ['pre-auth-detail', preAuthId], queryFn: () => get<Draft>(`/insurance/pre-auths/${preAuthId}`) });

  if (detail.isLoading) return <FormSkeleton />;
  if (!detail.data) return <Alert variant="destructive">{detail.error ? describeError(detail.error) : 'This request is not on file.'}</Alert>;

  return <DraftEditor key={detail.data.id} row={detail.data} returnTo={returnTo} />;
}

function DraftEditor({ row, returnTo }: { row: Draft; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initial = { diagnosis: row.diagnosis ?? '', procedure: row.procedure ?? '', notes: row.notes ?? '' };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      put(`/insurance/pre-auths/${row.id}/draft`, {
        ...(form.diagnosis.trim() && form.diagnosis.trim() !== (row.diagnosis ?? '') ? { diagnosis: form.diagnosis.trim() } : {}),
        ...(form.procedure.trim() && form.procedure.trim() !== (row.procedure ?? '') ? { procedure: form.procedure.trim() } : {}),
        ...(form.notes.trim() !== (row.notes ?? '') ? { notes: form.notes.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success('Request updated');
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-detail'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title="Correct the request"
      description="Only a request that has not been sent can be changed. The diagnosis is written as an ICD-10 code and a description, for example J18.9 - Pneumonia."
      icon={FilePen}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'request_details')} icon={FilePen} columns={1}>
        <Field label="Diagnosis" required>
          <Input value={form.diagnosis} onChange={(event) => setForm({ ...form, diagnosis: event.target.value })} placeholder="J18.9 - Pneumonia" />
        </Field>
        <Field label="Service" hint="Only used when the request has no billed lines">
          <Input value={form.procedure} onChange={(event) => setForm({ ...form, procedure: event.target.value })} />
        </Field>
        <Field label="Notes">
          <Textarea rows={2} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        </Field>
      </FormSection>
      {error && <Alert variant="destructive">{error}</Alert>}
      <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} cancelTo={returnTo} />
    </FormScreen>
  );
}
